import React, { useState, useEffect, useMemo } from 'react';
import { Button, TextField, Select, MenuItem, FormControl, Typography, Grid, Switch, Box, Card, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material';
import { styled } from '@mui/system';
import comms from '../comms';

// Styled components matching other components
const Container = styled(Box)({
  padding: '16px',
  maxWidth: '100vw',
  margin: '0 auto',
  backgroundColor: '#f8f9fa',
  minHeight: '100vh',
  paddingBottom: '80px', // Space for fixed submit button
});

const HeaderCard = styled(Card)({
  marginBottom: '16px',
  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  borderRadius: '8px',
});

const StyledTextField = styled(TextField)({
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'white',
    fontSize: '14px',
    '& fieldset': {
      borderColor: '#e0e0e0',
    },
    '&:hover fieldset': {
      borderColor: '#1976d2',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#1976d2',
    },
  },
  '& .MuiInputLabel-root': {
    fontSize: '14px',
  },
});

const StyledSelect = styled(Select)({
  backgroundColor: 'white',
  fontSize: '14px',
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#e0e0e0',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: '#1976d2',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#1976d2',
  },
});

const FieldLabel = styled(Typography)({
  fontSize: '14px',
  fontWeight: 500,
  color: '#333',
  marginBottom: '8px',
  display: 'flex',
  alignItems: 'center',
});

const SectionTitle = styled(Typography)({
  fontSize: '16px',
  fontWeight: 600,
  color: '#1976d2',
  marginBottom: '12px',
});

function BeginShift({
  onBeginShiftComplete, 
  onBack, 
  inspectionData, 
  odometerRequired = false,
  shiftId: providedShiftId,
  shiftObject,
  currentOdometerReading,
  selectedVehicle,
  fleetSettings,
  socket
}) {
  console.log('BeginShift received props:', { providedShiftId, shiftObject, inspectionData });
  const [formData, setFormData] = useState({});
  const [inspectionFields, setInspectionFields] = useState([]);
  const [shiftId, setShiftId] = useState(providedShiftId);
  const [images, setImages] = useState([]);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
  const [odometerReading, setOdometerReading] = useState(String(currentOdometerReading || ''));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [odometerError, setOdometerError] = useState('');
  const [showOdometerConfirm, setShowOdometerConfirm] = useState(false);

  // Compute min and max odometer values based on vehicle's current mileage
  // Uses configurable maxOdometerRange from fleetSettings (default 1000 miles)
  // If no previous reading exists (minValue === 0), any positive number is allowed
  // Backwards compatible: old servers include prevOutboundMileage/prevInboundMileage on shiftObject
  const { minOdometerValue, maxOdometerValue, hasPreviousReading } = useMemo(() => {
    const vehicleMileage = parseFloat(selectedVehicle?.mileage || selectedVehicle?.Mileage) || 0;
    // Old servers provided prev shift readings on shiftObject; new servers rely on vehicleMileage
    const prevOutbound = parseFloat(shiftObject?.prevOutboundMileage) || 0;
    const prevInbound = parseFloat(shiftObject?.prevInboundMileage) || 0;
    const minValue = Math.max(vehicleMileage, prevOutbound, prevInbound);
    
    // If no previous reading, allow any positive number (no bounds enforcement)
    if (minValue === 0) {
      return { minOdometerValue: 0, maxOdometerValue: null, hasPreviousReading: false };
    }
    
    // Use fleet configurable max range, default to 1000 miles
    const maxRange = fleetSettings?.maxOdometerRange || 1000;
    const maxValue = minValue + maxRange;
    
    console.log('BeginShift: Calculated odometer range:', {
      vehicleMileage,
      prevOutbound,
      prevInbound,
      minValue,
      maxValue,
      maxRange
    });
    
    return { minOdometerValue: minValue, maxOdometerValue: maxValue, hasPreviousReading: true };
  }, [selectedVehicle, shiftObject, fleetSettings?.maxOdometerRange]);

  // Compute effective odometer requirement - skip if outboundMileage is already populated
  const effectiveOdometerRequired = useMemo(() => {
    const isOdometerComplete = shiftObject?.outboundMileage != null && shiftObject.outboundMileage !== '';
    return odometerRequired && !isOdometerComplete;
  }, [odometerRequired, shiftObject?.outboundMileage]);

  // Initialize inspection data
  useEffect(() => {
    // If we've already completed successfully, don't reinitialize with old data
    if (isCompleted) {
      console.log('BeginShift already completed, skipping reinitialization');
      return;
    }
    
    if (inspectionData && inspectionData.data && inspectionData.data.length > 0) {
      const data = inspectionData.data;
      
      /*if (data[0]?.name === "**NO INSPECTION REQUIRED**" || data[0]?.name === "**COMPLETED**") {
        setIsCompleted(true);
        if (onBeginShiftComplete) {
          onBeginShiftComplete(true);
        }
        return;
      }*/
      
      // Check if BeginShift inspection is required
      // Required only when items have: isenditem=0, required=1, and performed=1
      const requiredBeginShiftItems = data.filter(item => 
        !item.isenditem && item.required && !item.performed
      );
      
      // Log odometer completion status for debugging
      if (!effectiveOdometerRequired && odometerRequired) {
        console.log('Odometer reading already complete (outboundMileage:', shiftObject?.outboundMileage, '), skipping odometer requirement');
      }
      
      if (requiredBeginShiftItems.length === 0 && !effectiveOdometerRequired) {
        // No required inspection items AND no odometer requirement, skip inspection and proceed
        console.log('No required BeginShift inspection items and no odometer requirement, proceeding...');
        setIsCompleted(true);
        if (onBeginShiftComplete) {
          onBeginShiftComplete(true);
        }
        return;
      }
      
      let beginShiftItems = [];
      if (requiredBeginShiftItems.length === 0 && effectiveOdometerRequired) {
        // No inspection items but odometer is required - show only odometer form
        console.log('No inspection items required, but odometer is required');
        setInspectionFields([]);
      } else {
        // Filter for all BeginShift items (isenditem === false or undefined)
        beginShiftItems = data.filter(item => !item.isenditem);
        beginShiftItems.sort((a, b) => a.name.localeCompare(b.name));
        setInspectionFields(beginShiftItems);
      }
      
      // Use providedShiftId prop first, then fall back to other sources
      const userSettings = JSON.parse(localStorage.getItem('userSettings')) || {};
      const finalShiftId = providedShiftId || userSettings.shiftId || inspectionData.shiftId;
      console.log('Setting shiftId in BeginShift:', { providedShiftId, userSettingsShiftId: userSettings.shiftId, inspectionDataShiftId: inspectionData.shiftId, finalShiftId });
      setShiftId(finalShiftId);
      
      const initialData = {};
      beginShiftItems.forEach((field, i) => {
        initialData[`input-${i}`] = field.type === 'boolean' ? false : '';
      });
      setFormData(initialData);
    } else if (!effectiveOdometerRequired) {
      // No inspection items at all (missing or empty inspectionData) AND no odometer
      // requirement. The server routed here on the inspectionRequired flag alone, so
      // there is nothing for the driver to do — auto-progress to the next screen
      // instead of showing an empty inspection form.
      console.log('BeginShift: No inspection items and no odometer requirement, auto-completing...');
      setIsCompleted(true);
      if (onBeginShiftComplete) {
        onBeginShiftComplete(true);
      }
    }
  }, [inspectionData, onBeginShiftComplete, providedShiftId, odometerRequired, isCompleted, shiftObject, effectiveOdometerRequired]);

  // Resolve the effective shiftId from every available source. The shift may be
  // created on the server before any inspection/odometer requirement exists, so
  // we must not rely on the inspection-data code path to populate it.
  const resolveShiftId = () => {
    const userSettings = JSON.parse(localStorage.getItem('userSettings') || '{}');
    return (
      providedShiftId ||
      shiftObject?.Id ||
      shiftObject?.id ||
      shiftObject?.shiftId ||
      shiftObject?.ShiftId ||
      userSettings.shiftId ||
      (inspectionData && inspectionData.shiftId) ||
      null
    );
  };

  // Keep shiftId in sync whenever any source of the id changes. Previously the id
  // was only resolved inside the inspection-data effect, so a freshly created shift
  // with no odometer/inspection requirement never populated shiftId.
  useEffect(() => {
    const resolved = resolveShiftId();
    if (resolved) {
      setShiftId((prev) => (prev === resolved ? prev : resolved));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providedShiftId, shiftObject, inspectionData]);

  // Validate required fields
  useEffect(() => {
    validateRequiredFields();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, images, odometerReading, effectiveOdometerRequired]); // Re-run validation every time formData, images, odometerReading, or effectiveOdometerRequired change

  // Handle dynamic input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: checked }));
  };

  const handleFileUpload = (e, index) => {
    const files = Array.from(e.target.files);
    const imagePromises = files.map((file) => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (event) => {
          const img = new Image();
          img.src = event.target.result;

          img.onload = () => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            const aspectRatio = img.height / img.width;
            const newWidth = 300;
            const newHeight = newWidth * aspectRatio;

            canvas.width = newWidth;
            canvas.height = newHeight;

            ctx.drawImage(img, 0, 0, newWidth, newHeight);

            const dataUrl = canvas.toDataURL(file.type);
            resolve(dataUrl);
          };

          img.onerror = (error) => reject(error);
        };

        reader.readAsDataURL(file);
      });
    });

    // Update the images state by appending new images instead of replacing them
    Promise.all(imagePromises).then((uploadedImages) => {
      setImages((prev) => [...prev, ...uploadedImages]); // Append new images to the state
      setFormData((prev) => ({
        ...prev,
        [`input-${index}`]: [...(prev[`input-${index}`] || []), ...uploadedImages] // Append to the specific input
      }));
    }).catch((error) => {
      console.error("Error processing images:", error);
    });
  };

  // Function to validate required fields and update odometer error message
  const validateRequiredFields = () => {
    // Skip validation if inspection is completed or being processed
    if (isCompleted || isSubmitting) {
      return;
    }
    
    let isComplete = true;
    let newOdometerError = '';
    
    // Check inspection fields
    inspectionFields.forEach((field, i) => {
      // Boolean fields are always answered (false is a valid response), so they never count as missing
      if (field.required && field.type !== 'boolean' && !formData[`input-${i}`]) {
        isComplete = false;
      }
    });
    
    // Check odometer if required with detailed error message.
    // Out-of-range readings produce a non-blocking WARNING; the driver confirms
    // them via a dialog on submit rather than being locked out of submission.
    if (effectiveOdometerRequired) {
      const odometerValue = parseFloat(odometerReading);
      if (!odometerReading || String(odometerReading).trim() === '' || isNaN(odometerValue)) {
        isComplete = false;
        newOdometerError = 'Odometer reading is required';
      } else if (odometerValue < 0) {
        isComplete = false;
        newOdometerError = 'Odometer reading must be a positive number';
      } else if (hasPreviousReading && odometerValue < minOdometerValue) {
        newOdometerError = `Below expected minimum (${minOdometerValue.toLocaleString()} mi) — you'll be asked to confirm`;
      } else if (hasPreviousReading && maxOdometerValue !== null && odometerValue > maxOdometerValue) {
        newOdometerError = `Above expected maximum (${maxOdometerValue.toLocaleString()} mi) — you'll be asked to confirm`;
      }
    }
    
    setOdometerError(newOdometerError);
    console.log('Validation complete:', isComplete)
    // Disable the submit button if any required fields are incomplete
    setIsSubmitDisabled(!isComplete);
  };

  // Returns true when the entered reading is outside the expected sanity-check range.
  const isOdometerOutOfRange = () => {
    if (!effectiveOdometerRequired || !hasPreviousReading) return false;
    const v = parseFloat(odometerReading);
    if (isNaN(v) || v < 0) return false; // empty/invalid handled separately
    return v < minOdometerValue || (maxOdometerValue !== null && v > maxOdometerValue);
  };

  // Handle form submission via socket
  const handleSubmit = async (skipRangeConfirm = false) => {
    if (isSubmitting) return;

    // Out-of-range odometer requires explicit confirmation before submitting
    if (!skipRangeConfirm && isOdometerOutOfRange()) {
      setShowOdometerConfirm(true);
      return;
    }

    setIsSubmitting(true);
    
    try {
      let data = [...inspectionFields];
      let complete = true;

      // Process inspection field data
      data.forEach((field, i) => {
        if (field.type === 'boolean') {
          data[i].result = formData[`input-${i}`] || false;
        } else if (['number', 'text', 'select', 'textField'].includes(field.type)) {
          data[i].result = formData[`input-${i}`] || '';
        } else if (field.type === 'image') {
          data[i].result = formData[`input-${i}`] || [];
        }

        // Boolean fields are always answered (false is a valid response), so they never count as missing
        if (field.required && field.type !== 'boolean' && !data[i].result) {
          complete = false;
        }
      });

      // Check odometer if required. Out-of-range values are allowed here because
      // the driver has already confirmed them via the out-of-range dialog.
      if (effectiveOdometerRequired) {
        const odometerValue = parseFloat(odometerReading);
        if (!odometerReading || String(odometerReading).trim() === '' || isNaN(odometerValue) || odometerValue < 0) {
          complete = false;
        }
      }

      if (!complete) {
        console.log("Inspection and/or odometer not completed");
        setIsSubmitting(false);
        return;
      }

      // Resolve the shiftId at submit time from every available source. The state
      // value may be stale/null when the shift was created without an odometer or
      // inspection requirement, so fall back through props/localStorage.
      const effectiveShiftId = shiftId || resolveShiftId();

      if (!effectiveShiftId) {
        console.error('Cannot submit inspection - no shiftId available');
        setIsSubmitting(false);
        return;
      }

      // Prepare submission data
      const submissionData = {
        shiftId: effectiveShiftId,
        inspectionData: inspectionFields.length > 0 ? data : null,
        odometerData: effectiveOdometerRequired ? {
          shiftId: effectiveShiftId,
          beginningOdometer: parseFloat(odometerReading)
        } : null,
        vehicleId: selectedVehicle?.Id || selectedVehicle?.id,
        userId: JSON.parse(localStorage.getItem('userSettings') || '{}').Id
      };

      console.log('Submitting inspection with data:', submissionData);

      // Submit via socket
      if (socket && socket.connected) {
        socket.emit('submitInspection', submissionData, (response) => {
          console.log('Socket response received:', response);
          setIsSubmitting(false);
          
          // Check for success - handle both simple and nested response structures
          const isSuccess = response && (
            response.success === true || 
            (response.results && response.results.inspection && response.results.inspection.success === true)
          );
          
          if (isSuccess) {
            console.log('Inspection submitted successfully');
            setIsCompleted(true);
            
            // Call completion callback with completion data object
            if (onBeginShiftComplete) {
              onBeginShiftComplete({
                inspectionCompleted: true,
                odometerCompleted: true
              });
            }
          } else {
            console.error('Failed to submit inspection:', response?.error || response?.message || 'Unknown error');
          }
        });
      } else if (comms.socketConnection) {
        // Fallback to comms socket connection
        comms.socketConnection.emit('submitInspection', submissionData, (response) => {
          console.log('Comms socket response received:', response);
          setIsSubmitting(false);
          
          // Check for success - handle both simple and nested response structures
          const isSuccess = response && (
            response.success === true || 
            (response.results && response.results.inspection && response.results.inspection.success === true)
          );
          
          if (isSuccess) {
            console.log('Inspection submitted successfully');
            setIsCompleted(true);
            
            // Call completion callback with completion data object
            if (onBeginShiftComplete) {
              onBeginShiftComplete({
                inspectionCompleted: true,
                odometerCompleted: true
              });
            }
          } else {
            console.error('Failed to submit inspection:', response?.error || response?.message || 'Unknown error');
          }
        });
      } else {
        console.error('Socket connection not available');
        setIsSubmitting(false);
      }

    } catch (error) {
      console.error("Error submitting inspection:", error);
      setIsSubmitting(false);
    }
  };

  return (
    <Container>
      {/* Single consolidated card */}
      <HeaderCard>
        <Box p={3}>
          {/* Header */}
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#333', mb: 3 }}>
            {effectiveOdometerRequired ? 'Vehicle Inspection & Odometer' : 'Vehicle Inspection'}
          </Typography>

          {/* Odometer Section */}
          {effectiveOdometerRequired && (
            <Box mb={3}>
              <SectionTitle>Beginning Odometer Reading</SectionTitle>
              <StyledTextField
                type="number"
                value={odometerReading}
                onChange={(e) => setOdometerReading(e.target.value)}
                placeholder={hasPreviousReading ? `Enter mileage (min: ${minOdometerValue.toLocaleString()})` : "Enter current mileage"}
                fullWidth
                size="small"
                error={!!odometerError}
                inputProps={{ 
                  min: hasPreviousReading ? minOdometerValue : 0, 
                  max: hasPreviousReading && maxOdometerValue ? maxOdometerValue : undefined, 
                  step: 0.1 
                }}
                helperText={odometerError || (hasPreviousReading 
                  ? `Valid range: ${minOdometerValue.toLocaleString()} - ${maxOdometerValue.toLocaleString()} miles`
                  : "Enter odometer reading to nearest tenth of a mile"
                )}
              />
            </Box>
          )}

          {/* Inspection Items */}
          {inspectionFields.length > 0 && (
            <Box mb={3}>
              <SectionTitle>Inspection Items</SectionTitle>
              <Grid container spacing={2}>
                {inspectionFields.map((field, index) => (
                  <Grid item xs={12} key={index}>
                    <FieldLabel>
                      {field.name}
                      {!!field.required && <span style={{ color: '#d32f2f', marginLeft: '4px' }}>*</span>}
                    </FieldLabel>
                    
                    {field.type === 'boolean' ? (
                      <Box display="flex" alignItems="center">
                        <Switch
                          name={`input-${index}`}
                          checked={formData[`input-${index}`] || false}
                          onChange={handleCheckboxChange}
                          color="primary"
                          size="small"
                        />
                        <Typography variant="body2" sx={{ ml: 1 }}>
                          {formData[`input-${index}`] ? 'Yes' : 'No'}
                        </Typography>
                      </Box>
                    ) : field.type === 'number' ? (
                      <StyledTextField
                        name={`input-${index}`}
                        type="number"
                        value={formData[`input-${index}`] || ''}
                        onChange={handleInputChange}
                        fullWidth
                        size="small"
                        inputProps={{
                          min: field.min,
                          max: field.max,
                        }}
                      />
                    ) : field.type === 'select' ? (
                      <FormControl fullWidth size="small">
                        <StyledSelect
                          value={formData[`input-${index}`] || ''}
                          name={`input-${index}`}
                          onChange={handleInputChange}
                          displayEmpty
                        >
                          <MenuItem value="">
                            <em>Select an option</em>
                          </MenuItem>
                          {field.description.split(',').map((option, idx) => (
                            <MenuItem key={idx} value={option.trim()}>
                              {option.trim()}
                            </MenuItem>
                          ))}
                        </StyledSelect>
                      </FormControl>
                    ) : field.type === 'text' ? (
                      <StyledTextField
                        name={`input-${index}`}
                        type="text"
                        value={formData[`input-${index}`] || ''}
                        onChange={handleInputChange}
                        fullWidth
                        size="small"
                      />
                    ) : field.type === 'textField' ? (
                      <StyledTextField
                        name={`input-${index}`}
                        value={formData[`input-${index}`] || ''}
                        onChange={handleInputChange}
                        fullWidth
                        multiline
                        rows={3}
                        size="small"
                      />
                    ) : field.type === 'image' ? (
                      <Box>
                        <Button
                          variant="outlined"
                          color="primary"
                          onClick={() => document.getElementById(`imageInput-${index}`).click()}
                          sx={{ textTransform: 'none', mb: 1 }}
                        >
                          Take Photos
                        </Button>
                        <input
                          type="file"
                          id={`imageInput-${index}`}
                          accept="image/*"
                          multiple
                          hidden
                          onChange={(e) => handleFileUpload(e, index)}
                        />
                        {formData[`input-${index}`] && formData[`input-${index}`].length > 0 && (
                          <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
                            {formData[`input-${index}`].map((src, idx) => (
                              <Box
                                key={idx}
                                component="img"
                                src={src}
                                alt={`inspection-${index}-${idx}`}
                                sx={{
                                  width: 60,
                                  height: 60,
                                  objectFit: 'cover',
                                  borderRadius: 1,
                                  border: '1px solid #e0e0e0'
                                }}
                              />
                            ))}
                          </Box>
                        )}
                      </Box>
                    ) : null}
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          {/* Bottom Buttons */}
          <Box display="flex" gap={2} mt={3}>
            {onBack && (
              <Button
                variant="text"
                onClick={onBack}
                sx={{ 
                  flex: 1,
                  height: '48px',
                  fontSize: '16px',
                  fontWeight: 600,
                  textTransform: 'none',
                  color: '#6b7280'
                }}
              >
                Back
              </Button>
            )}
            <Button
              variant="contained"
              color="primary"
              onClick={() => handleSubmit()}
              disabled={isSubmitDisabled || isSubmitting}
              sx={{ 
                flex: 1,
                height: '48px',
                fontSize: '16px',
                fontWeight: 600,
                textTransform: 'none'
              }}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Inspection'}
            </Button>
          </Box>
        </Box>
      </HeaderCard>

      {/* Out-of-range odometer confirmation */}
      <Dialog open={showOdometerConfirm} onClose={() => setShowOdometerConfirm(false)}>
        <DialogTitle>Confirm Odometer Reading</DialogTitle>
        <DialogContent>
          <DialogContentText>
            The reading you entered ({Number(odometerReading || 0).toLocaleString()} mi) is outside the
            expected range{hasPreviousReading && maxOdometerValue !== null
              ? ` (${minOdometerValue.toLocaleString()} – ${maxOdometerValue.toLocaleString()} mi)`
              : ''}. Please confirm this is the correct reading.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowOdometerConfirm(false)} color="inherit">
            Go Back
          </Button>
          <Button
            onClick={() => { setShowOdometerConfirm(false); handleSubmit(true); }}
            variant="contained"
            color="primary"
          >
            Confirm Reading
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default BeginShift;
